import { UseQueryResult, useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import { EnvironmentId } from '@/react/portainer/environments/types';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';

import { queryKeys } from '../../../applications/queries/query-keys';

/**
 * @returns a custom resource from the Kubernetes API proxy
 * when yaml is set to true, the expected return type is a string
 */
export function useCustomResource<T = unknown>(
  environmentId: EnvironmentId,
  namespace: string,
  name: string,
  definition: string,
  options?: { autoRefreshRate?: number; yaml?: boolean }
): UseQueryResult<T> {
  return useQuery(
    queryKeys.customResource(
      environmentId,
      namespace,
      name,
      definition,
      options?.yaml
    ),
    () =>
      getCustomResource<T>(
        environmentId,
        namespace,
        name,
        definition,
        options?.yaml
      ),
    {
      enabled: !!environmentId && !!namespace && !!name && !!definition,
      ...withError('Unable to retrieve custom resource'),
      refetchInterval() {
        return options?.autoRefreshRate ?? false;
      },
    }
  );
}

async function getCustomResource<T = unknown>(
  environmentId: EnvironmentId,
  namespace: string,
  name: string,
  definition: string,
  yaml?: boolean
): Promise<T> {
  try {
    const path = buildPath(namespace, name);
    const { data } = await axios.get<T>(
      `/kubernetes/${environmentId}/customresources/${path}`,
      {
        params: {
          namespace,
          name,
          definition,
          format: yaml ? 'yaml' : 'json',

          // this logic is to get the latest YAML response
          // axios-cache-adapter looks for the response headers to determine if the response should be cached
          // to avoid writing requestInterceptor, adding a query param to the request url
          ...(yaml && {
            _: Date.now(),
          }),
        },
        headers: { Accept: yaml ? 'application/yaml' : 'application/json' },
      }
    );
    return data;
  } catch (err) {
    throw parseAxiosError(err);
  }
}

function buildPath(namespace: string, name: string) {
  if (namespace) {
    return `${namespace}/${name}`;
  }
  return name;
}
